/**
 * Error Handler Utility
 * Manages application-wide error handling and logging
 */

import { apiClient } from './api';

// Suppress specific errors in console based on context
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;

// Track if we've already initialized error handling
let errorHandlingInitialized = false;

export function initializeErrorHandling() {
  if (errorHandlingInitialized) {
    return;
  }

  // Override console.error to filter out expected errors in Mock Mode
  console.error = (...args: any[]) => {
    const message = args[0];
    const isMockMode = apiClient.isMockMode();

    // Filter out fetch errors in Mock Mode
    if (isMockMode) {
      if (
        typeof message === 'string' && 
        (message.includes('Failed to fetch') || 
         message.includes('API Request failed') ||
         message.includes('supabase.co/functions') ||
         message.includes('API Request failed'))
      ) {
        // Suppress these errors in mock mode - they're expected
        return;
      }

      // Also filter if the message is an object with error details
      if (typeof message === 'object' && message !== null) {
        const str = JSON.stringify(message);
        if (str.includes('Failed to fetch') || str.includes('supabase.co/functions') || str.includes('API Request failed')) {
          return;
        }
      }
    }

    // Allow other errors to be logged normally
    originalConsoleError.apply(console, args);
  };

  // Keep warnings as they are, but add context for Mock Mode
  console.warn = (...args: any[]) => {
    originalConsoleWarn.apply(console, args);
  };

  errorHandlingInitialized = true;
  
  // Mock mode message removed for cleaner console
}

export function restoreConsole() {
  console.error = originalConsoleError;
  console.warn = originalConsoleWarn;
  errorHandlingInitialized = false;
}

// Global error boundary helper
export function handleGlobalError(error: Error, errorInfo?: any) {
  console.error('Global error:', error, errorInfo);
}

// API error classifier
export function isNetworkError(error: any): boolean {
  if (error instanceof Error) {
    return (
      error.message.includes('Failed to fetch') ||
      error.message.includes('Network request failed') ||
      error.message.includes('NetworkError')
    );
  }
  return false;
}

export function isAPIError(error: any): boolean {
  if (error instanceof Error) {
    return error.message.includes('API Error');
  }
  return false;
}

// Format error for user display
export function formatUserError(error: any): string {
  if (isNetworkError(error)) {
    return 'No se pudo conectar al servidor. Por favor, verifica tu conexión.';
  }

  if (isAPIError(error)) {
    return 'Error en el servidor. Por favor, intenta nuevamente.';
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Ocurrió un error inesperado';
}